import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Spinner, Alert, Table, Badge } from 'react-bootstrap';
import { searchProductAcrossStores } from '../services/priceService';
import { useHomeLocation } from '../contexts/HomeLocationContext';
import ReportButton from './ReportButton';
import './ProductSearch.css';

// Map chain IDs to display names
const chainDisplayNames = {
  shufersal: 'שופרסל',
  victory: 'ויקטורי',
  carrefour: 'קרפור',
  'rami-levy': 'רמי לוי',
  mega: 'מגה',
  'osher-ad': 'אושר עד',
  'tiv-taam': 'טיב טעם',
  ampm: 'AM:PM',
  keshet: 'קשת',
  yellow: 'יילו',
  freshmarket: 'פרשמרקט',
  'good-pharm': 'גוד פארם',
  'super-pharm': 'סופר פארם'
};

const getChainDisplayName = (chainId) => {
  return chainDisplayNames[chainId] || chainId;
};

// Map chain IDs to Bootstrap badge colors
const chainColors = {
  shufersal: 'danger',      // red
  victory: 'warning',       // orange
  carrefour: 'primary',     // blue
  'rami-levy': 'success',   // green
  mega: 'secondary',        // gray
  'osher-ad': 'light',      // light gray
  'tiv-taam': 'dark',       // dark
  ampm: 'info',             // light blue
  keshet: 'success',        // green
  yellow: 'warning',        // orange/yellow (Yellow brand color)
  freshmarket: 'info',      // light blue
  'good-pharm': 'success',  // green (pharmacy color)
  'super-pharm': 'primary'  // blue (Super-Pharm brand color)
};

const getChainColor = (chainId) => {
  return chainColors[chainId] || 'info';
};

const ProductSearch = ({ initialBarcode = '' }) => {
  const { homeLocation } = useHomeLocation();
  const [searchTerm, setSearchTerm] = useState(initialBarcode);
  const [maxDistance, setMaxDistance] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (initialBarcode && /^\d+$/.test(initialBarcode.trim())) {
      setSearchTerm(initialBarcode.trim());
      runSearch(initialBarcode.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBarcode]);

  const runSearch = async (term) => {
    try {
      setIsSearching(true);
      setError(null);
      setHasSearched(true);
      const results = await searchProductAcrossStores(term, maxDistance, homeLocation);
      setSearchResults(results);
      setIsSearching(false);
    } catch (err) {
      console.error('Error looking up product:', err);
      setError('Failed to lookup product. Please try again.');
      setIsSearching(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    const trimmedTerm = searchTerm.trim();

    if (!trimmedTerm) {
      setError('Please enter an item code (barcode) to search');
      return;
    }

    // Validate that it's a numeric barcode
    if (!/^\d+$/.test(trimmedTerm)) {
      setError('Please enter a valid item code (barcode). Only numeric codes are supported.');
      return;
    }

    await runSearch(trimmedTerm);
  };

  // Group results by item code (for barcode search, there's typically just one product)
  const groupedResults = searchResults.reduce((acc, item) => {
    if (!acc[item.itemCode]) {
      acc[item.itemCode] = {
        itemCode: item.itemCode,
        itemName: item.itemName,
        manufacturer: item.manufacturer,
        stores: []
      };
    }

    if (item.price == null) return acc;

    acc[item.itemCode].stores.push({
      storeId: item.storeId,
      storeName: item.storeName,
      chain: item.chain,
      price: item.price,
      unitPrice: item.unitPrice,
      unitMeasure: item.unitMeasure,
      promotionPrice: item.promotionPrice,
      promotionDescription: item.promotionDescription,
      promotionSavings: item.promotionSavings,
      promotionSavingsPercentage: item.promotionSavingsPercentage,
      promotionType: item.promotionType,
      promotionMinQty: item.promotionMinQty,
      distance_km: item.distance_km,
      distance_text: item.distance_text
    });

    // Sort stores by final price (promotional price if available, otherwise regular price)
    acc[item.itemCode].stores.sort((a, b) => {
      const finalPriceA = a.promotionPrice && a.promotionPrice < a.price ? a.promotionPrice : a.price;
      const finalPriceB = b.promotionPrice && b.promotionPrice < b.price ? b.promotionPrice : b.price;
      return (finalPriceA ?? Infinity) - (finalPriceB ?? Infinity);
    });

    return acc;
  }, {});

  // Convert to array (for barcode search, there should be only one product)
  const sortedResults = Object.values(groupedResults);

  return (
    <Card className="mb-4">
      <Card.Header>
        <h4 className="mb-0" style={{ fontSize: 'var(--font-title)', fontWeight: 600, letterSpacing: '-0.3px' }}>Barcode Lookup — Find Cheapest Store</h4>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSearch}>
          <Form.Group className="mb-3">
            <Form.Label>Item Code (Barcode)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter item code/barcode (e.g., 7290000964980)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              inputMode="numeric"
              pattern="[0-9]*"
            />
            <Form.Text className="text-muted">
              Enter a product barcode to find which store sells it for the lowest price.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Maximum Distance from Home (km){' '}
              {homeLocation && (
                <span className="badge bg-primary ms-1" style={{ fontSize: '11px', fontWeight: 500 }}>
                  Custom location
                </span>
              )}
            </Form.Label>
            <Form.Select
              value={maxDistance || ''}
              onChange={(e) => setMaxDistance(e.target.value ? parseFloat(e.target.value) : null)}
            >
              <option value="">All stores (no limit)</option>
              <option value="1">1 km</option>
              <option value="2">2 km</option>
              <option value="5">5 km</option>
              <option value="10">10 km</option>
              <option value="20">20 km</option>
            </Form.Select>
            <Form.Text className="text-muted">
              {homeLocation
                ? `From: ${homeLocation.label}`
                : 'Only show stores within this distance from your home address.'}
            </Form.Text>
          </Form.Group>

          <div className="d-grid">
            <Button variant="success" type="submit" disabled={isSearching}>
              {isSearching ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Looking up...
                </>
              ) : (
                'Lookup Barcode'
              )}
            </Button>
          </div>
        </Form>
        
        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}
        
        {isSearching && (
          <div className="text-center my-4">
            <Spinner animation="border" role="status" variant="primary" />
            <p className="mt-2">Looking up barcode across all stores...</p>
          </div>
        )}

        {!isSearching && hasSearched && searchResults.length === 0 && (
          <Alert variant="info" className="mt-3">
            No product found with barcode "{searchTerm}". Make sure you entered the correct item code.
          </Alert>
        )}
        
        {!isSearching && sortedResults.length > 0 && (
          <div className="mt-4">
            <h5>Results for Barcode "{searchTerm}"</h5>
            <p className="text-muted">Found in {new Set(searchResults.map(r => r.storeId)).size} stores.</p>
            
            {sortedResults.map((product) => (
              <Card key={product.itemCode} className="mb-3">
                <Card.Header className="bg-light">
                  <h6 className="mb-0 rtl-text">{product.itemName}</h6>
                  {product.manufacturer && (
                    <small className="text-muted rtl-text">יצרן: {product.manufacturer}</small>
                  )}
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Chain</th>
                        <th>Store</th>
                        <th>Distance</th>
                        <th>Regular Price (₪)</th>
                        <th>Promo Price (₪)</th>
                        <th>Unit Price</th>
                        <th>Savings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.stores.map((store, index) => {
                        // Calculate savings compared to the most expensive store (using final prices)
                        const mostExpensiveStore = product.stores[product.stores.length - 1];
                        const highestFinalPrice = mostExpensiveStore.promotionPrice && mostExpensiveStore.promotionPrice < mostExpensiveStore.price
                          ? mostExpensiveStore.promotionPrice
                          : mostExpensiveStore.price;

                        // Get current store's final price
                        const currentFinalPrice = store.promotionPrice && store.promotionPrice < store.price
                          ? store.promotionPrice
                          : store.price;

                        const savings = highestFinalPrice - currentFinalPrice;
                        const savingsPercent = (savings / highestFinalPrice) * 100;

                        // Check if item has a promotion
                        const hasPromotion = store.promotionPrice && store.promotionPrice < store.price;

                        return (
                          <tr key={`${store.chain}_${store.storeId}`} className={index === 0 ? 'table-success' : ''}>
                            <td dir="rtl">
                              <span className="chain-badge">
                                <span className={`chain-dot chain-dot-${store.chain}`}></span>
                                {getChainDisplayName(store.chain)}
                              </span>
                            </td>
                            <td dir="rtl">
                              {store.storeName}
                              {index === 0 && (
                                <Badge bg="success" className="ms-2 cheapest-badge">Cheapest</Badge>
                              )}
                              <div><small className="text-muted font-monospace">{store.chain}_{store.storeId}</small></div>
                            </td>
                            <td>
                              {store.distance_text || (store.distance_km ? `${store.distance_km.toFixed(1)} km` : '-')}
                            </td>
                            <td>
                              {hasPromotion ? (
                                <span className="text-muted text-decoration-line-through">
                                  {store.price.toFixed(2)}
                                </span>
                              ) : (
                                <span>{store.price.toFixed(2)}</span>
                              )}
                              <ReportButton itemCode={product.itemCode} storeId={store.storeId} chain={store.chain} price={store.price} itemName={product.itemName} />
                            </td>
                            <td>
                              {hasPromotion ? (
                                <div>
                                  <strong className="text-danger">{store.promotionPrice.toFixed(2)}</strong>
                                  <Badge bg="danger" className="ms-2 promo-badge">
                                    {store.promotionSavingsPercentage.toFixed(0)}% OFF
                                  </Badge>
                                  {store.promotionMinQty && store.promotionMinQty > 1 && (
                                    <div className="small text-muted rtl-text" dir="rtl">
                                      {store.promotionDescription}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {store.unitPrice ? (
                                <span>{store.unitPrice.toFixed(2)} ₪/{store.unitMeasure}</span>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>
                              {index === product.stores.length - 1 ? (
                                '-'
                              ) : (
                                <span className="text-success">
                                  Save {savings.toFixed(2)} ₪ ({savingsPercent.toFixed(1)}%)
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ProductSearch;
